import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables:', {
    url: !!supabaseUrl,
    key: !!supabaseAnonKey
  });
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storageKey: 'supabase.auth.token',
    storage: window.localStorage
  },
  global: {
    headers: {
      'X-Client-Info': 'supabase-js-v2'
    }
  }
});

// Listen for auth state changes
supabase.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_OUT') {
    // Clear local storage on sign out
    window.localStorage.removeItem('supabase.auth.token');
  }
});

// Helper function to handle auth errors
const handleAuthError = async (error: any) => {
  if (error?.message?.includes('JWT') || error?.message?.includes('token') || error?.status === 403) {
    // Try to refresh the session
    const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
    
    if (refreshError || !session) {
      // If refresh fails, sign out the user
      await supabase.auth.signOut();
      throw new Error('Session expired. Please sign in again.');
    }
    
    return session;
  }
  throw error;
};

// Auth functions
export const auth = {
  signUp: async (email: string, password: string, userData: any) => {
    try {
      // Check if user already exists
      const { data: existingUser } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .maybeSingle();

      if (existingUser) {
        throw new Error('User already exists');
      }

      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: userData.name,
            phone: userData.phone
          }
        }
      });

      if (authError) throw authError;

      if (!authData.user) {
        throw new Error('Failed to create auth user');
      }

      // Create profile in users table
      const { error: userError } = await supabase
        .from('users')
        .upsert({
          id: authData.user.id,
          email: email,
          name: userData.name,
          phone: userData.phone,
          account_type: userData.account_type || 'individual',
          notification_preferences: {
            email: true,
            sms: true,
            push: true
          }
        });

      if (userError) {
        throw new Error(`Failed to create user profile: ${userError.message}`);
      }

      return { data: authData, error: null };
    } catch (error) {
      console.error('Error signing up:', error);
      return { data: null, error };
    }
  },

  signIn: async (email: string, password: string) => {
    try {
      const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) throw signInError;

      if (!authData.user) {
        throw new Error('Authentication failed');
      }

      // Fetch user profile
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .maybeSingle();

      if (userError) {
        await handleAuthError(userError);
        console.error('Error fetching user profile:', userError);
        return { 
          data: { 
            session: authData.session, 
            user: authData.user 
          }, 
          error: null 
        };
      }

      // If no user profile found, return auth data only
      if (!userData) {
        return { 
          data: { 
            session: authData.session, 
            user: authData.user 
          }, 
          error: null 
        };
      }

      return { 
        data: { 
          session: authData.session, 
          user: userData 
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Error signing in:', error);
      return { data: null, error };
    }
  },

  signOut: async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Clear local storage
      window.localStorage.removeItem('supabase.auth.token');
      return { error: null };
    } catch (error) {
      console.error('Error signing out:', error);
      return { error };
    }
  },

  getCurrentUser: async () => {
    try {
      // First check if we have a session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        await handleAuthError(sessionError);
        throw sessionError;
      }
      
      if (!session) {
        const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError || !refreshedSession) {
          return { user: null, error: null };
        }
      }

      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error) {
        await handleAuthError(error);
        return { user: null, error };
      }

      if (!user) {
        return { user: null, error: null };
      }

      // Fetch user profile
      const { data: userData, error: profileError } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (profileError) {
        await handleAuthError(profileError);
        console.error('Error fetching user profile:', profileError);
        return { user, error: null };
      }

      return { user: userData || user, error: null };
    } catch (error) {
      console.error('Error getting current user:', error);
      return { user: null, error };
    }
  },

  updatePassword: async (newPassword: string) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        await handleAuthError(error);
      }

      return { data, error };
    } catch (error) {
      console.error('Error updating password:', error);
      return { data: null, error };
    }
  }
};

// Database functions with error handling
export const db = {
  getUser: async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        await handleAuthError(error);
        throw error;
      }

      return { data: data || null, error: null };
    } catch (error) {
      console.error('Error fetching user:', error);
      return { data: null, error };
    }
  },

  updateUser: async (userId: string, updates: any) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', userId)
        .select()
        .maybeSingle();

      if (error) {
        await handleAuthError(error);
      }

      return { data: data || null, error };
    } catch (error) {
      console.error('Error updating user:', error);
      return { data: null, error };
    }
  },

  createProject: async (projectData: any) => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .insert(projectData)
        .select()
        .maybeSingle();

      if (error) {
        await handleAuthError(error);
      }

      return { data: data || null, error };
    } catch (error) {
      console.error('Error creating project:', error);
      return { data: null, error };
    }
  },

  getProjects: async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          bill_data (*),
          location_data (*),
          solar_designs (*),
          financial_data (*),
          site_visits (*)
        `)
        .eq('user_id', userId);

      if (error) {
        await handleAuthError(error);
        console.error('Error fetching projects:', error);
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching projects:', error);
      return { data: [], error };
    }
  },

  updateProject: async (projectId: string, updates: any) => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .update(updates)
        .eq('id', projectId)
        .select()
        .maybeSingle();

      if (error) {
        await handleAuthError(error);
      }

      return { data: data || null, error };
    } catch (error) {
      console.error('Error updating project:', error);
      return { data: null, error };
    }
  },

  scheduleSiteVisit: async (visitData: any) => {
    try {
      const { data, error } = await supabase
        .from('site_visits')
        .insert(visitData)
        .select()
        .maybeSingle();

      if (error) {
        await handleAuthError(error);
      }

      return { data: data || null, error };
    } catch (error) {
      console.error('Error scheduling site visit:', error);
      return { data: null, error };
    }
  },

  updateSiteVisit: async (visitId: string, updates: any) => {
    try {
      const { data, error } = await supabase
        .from('site_visits')
        .update(updates)
        .eq('id', visitId)
        .select()
        .maybeSingle();

      if (error) {
        await handleAuthError(error);
      }

      return { data: data || null, error };
    } catch (error) {
      console.error('Error updating site visit:', error);
      return { data: null, error };
    }
  },

  uploadDocument: async (file: File, path: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .upload(path, file);

      if (error) {
        await handleAuthError(error);
      }

      return { data, error };
    } catch (error) {
      console.error('Error uploading document:', error);
      return { data: null, error };
    }
  },

  getDocumentUrl: async (path: string) => {
    try {
      const { data } = await supabase.storage
        .from('documents')
        .getPublicUrl(path);

      return data.publicUrl;
    } catch (error) {
      console.error('Error getting document URL:', error);
      return null;
    }
  },

  createUserAndProject: async (contactData: {
    name: string;
    email: string;
    phone: string;
    password: string;
    accountType: string;
    companyName?: string;
    crNumber?: string;
    websiteUrl?: string;
  }, energyProfileData: {
    objectives: string[];
    energyMix: { [key: string]: number };
  }, userType: string, billData?: {
    electricity_bill?: string | null;
    monthly_usage?: number;
    monthly_bill?: number;
    utility_provider?: string;
    yearly_consumption?: number;
    morning_consumption_pct?: number;
    effective_yearly_consumption?: number;
    location_photos?: string[];
  }): Promise<{ data: { user: any; project: any; projectId: string } | null; error: any }> => {
    try {
      // Validate energy profile data
      if (!energyProfileData.objectives || energyProfileData.objectives.length === 0) {
        throw new Error('At least one objective is required');
      }
      if (energyProfileData.objectives.length > 3) {
        throw new Error('Maximum 3 objectives allowed');
      }
      if (!energyProfileData.energyMix || Object.keys(energyProfileData.energyMix).length === 0) {
        throw new Error('At least one energy source is required');
      }

      // Validate energy mix percentages
      const totalPercentage = Object.values(energyProfileData.energyMix).reduce((sum, val) => sum + val, 0);
      if (Math.abs(totalPercentage - 100) > 0.01) {
        throw new Error(`Energy mix percentages must total 100%. Current total: ${totalPercentage.toFixed(1)}%`);
      }

      // Check if user already exists
      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .eq('email', contactData.email)
        .maybeSingle();

      if (existingUser) {
        throw new Error('User already registered');
      }

      // Create new user account (without signing in)
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: contactData.email,
        password: contactData.password,
        options: {
          data: {
            name: contactData.name,
            phone: contactData.phone
          }
        }
      });

      if (signUpError) {
        throw signUpError;
      }

      if (!signUpData?.user) {
        throw new Error('Failed to create user account');
      }

      // Create or update user profile in public.users
      const { error: userError } = await supabase
        .from('users')
        .upsert({
          id: signUpData.user.id,
          email: contactData.email,
          name: contactData.name,
          phone: contactData.phone,
          account_type: contactData.accountType,
          company_name: contactData.companyName || null,
          cr_number: contactData.crNumber || null,
          notification_preferences: {
            email: true,
            sms: true,
            push: true
          }
        });

      if (userError) {
        throw new Error(`Failed to create/update user profile: ${userError.message}`);
      }

      // Create or update corporate_info if account type is corporate
      if (contactData.accountType === 'corporate') {
        const { error: corporateError } = await supabase
          .from('corporate_info')
          .upsert({
            user_id: signUpData.user.id,
            company_name: contactData.companyName || '',
            cr_number: contactData.crNumber || '',
            vat_number: '', // Will be filled later if needed
            industry: userType,
            website: contactData.websiteUrl || '',
            address: '' // Will be filled later
          });

        if (corporateError) {
          console.warn('Failed to create/update corporate info:', corporateError);
          // Don't throw error, continue with project creation
        }
      }

      // Generate project ID
      const projectId = crypto.randomUUID();

      // Prepare project data
      const projectData: any = {
        id: projectId,
        user_id: signUpData.user.id,
        project_status: 'draft',
        user_type: userType,
        solar_design: {
          objective: energyProfileData.objectives,
          energy_mix: energyProfileData.energyMix
        },
        contact_info: {
          name: contactData.name,
          email: contactData.email,
          phone: contactData.phone,
          account_type: contactData.accountType,
          company_name: contactData.companyName,
          cr_number: contactData.crNumber,
          website_url: contactData.websiteUrl
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      if (billData) {
        projectData.bill_data = billData;
      }

      // Create project
      const { data: projectData_result, error: projectError } = await supabase
        .from('projects')
        .insert(projectData)
        .select()
        .single();

      if (projectError) {
        throw new Error(`Failed to create project: ${projectError.message}`);
      }

      return { 
        data: { 
          user: signUpData.user, 
          project: projectData_result,
          projectId: projectId
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Error creating user and project:', error);
      return { data: null, error };
    }
  },

  upsertProjectEnergyProfile: async (projectId: string, userId: string, energyProfileData: {
    objectives: string[];
    energyMix: { [key: string]: number };
  }) => {
    try {
      // Validate input data
      if (!energyProfileData.objectives || energyProfileData.objectives.length === 0) {
        throw new Error('At least one objective is required');
      }
      if (energyProfileData.objectives.length > 3) {
        throw new Error('Maximum 3 objectives allowed');
      }
      if (!energyProfileData.energyMix || Object.keys(energyProfileData.energyMix).length === 0) {
        throw new Error('At least one energy source is required');
      }

      // Validate energy mix percentages
      const totalPercentage = Object.values(energyProfileData.energyMix).reduce((sum, val) => sum + val, 0);
      if (Math.abs(totalPercentage - 100) > 0.01) {
        throw new Error(`Energy mix percentages must total 100%. Current total: ${totalPercentage.toFixed(1)}%`);
      }

      // Check if project exists
      const { data: existingProject, error: fetchError } = await supabase
        .from('projects')
        .select('id, solar_design')
        .eq('id', projectId)
        .eq('user_id', userId)
        .maybeSingle();

      if (fetchError) {
        await handleAuthError(fetchError);
        throw fetchError;
      }

      // Prepare energy profile data for JSONB storage
      const energyProfile = {
        objective: energyProfileData.objectives,
        energy_mix: energyProfileData.energyMix
      };

      if (existingProject) {
        // Update existing project - merge with existing solar_design
        const existingSolarDesign = existingProject.solar_design || {};
        const updatedSolarDesign = {
          ...existingSolarDesign,
          ...energyProfile
        };

        const { data, error } = await supabase
          .from('projects')
          .update({
            solar_design: updatedSolarDesign,
            updated_at: new Date().toISOString()
          })
          .eq('id', projectId)
          .eq('user_id', userId)
          .select()
          .single();

        if (error) {
          await handleAuthError(error);
          throw error;
        }

        return { data, error: null };
      } else {
        // Create new project with energy profile
        const { data, error } = await supabase
          .from('projects')
          .insert({
            id: projectId,
            user_id: userId,
            project_status: 'draft',
            solar_design: energyProfile,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single();

        if (error) {
          await handleAuthError(error);
          throw error;
        }

        return { data, error: null };
      }
    } catch (error) {
      console.error('Error upserting project energy profile:', error);
      return { data: null, error };
    }
  },

  updateProjectData: async (projectId: string, userId: string, updateData: {
    location_data?: any;
    solar_design?: any;
    bill_data?: any;
    files?: any;
    location_photos?: string[];
  }) => {
    try {
      // Prepare update data with timestamp
      const updatePayload = {
        ...updateData,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('projects')
        .update(updatePayload)
        .eq('id', projectId)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) {
        await handleAuthError(error);
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating project data:', error);
      return { data: null, error };
    }
  }
};
